import { useState, useEffect, useMemo } from 'react';
import { api } from '../lib/api.js';
import { DailyCashCountsPanel } from './DailyCashCountsPanel.jsx';

// ─── Reports → Cash counts (weekly count + certify) ────────────────────────
// Focused surface for entering a store's weekly counted_cash and certifying
// that the count is done. Reachable by anyone with `enter_cash_count`
// (owner/admin/manager/shift lead). The server scopes rows to the viewer's
// assigned locations and strips expected_cash/cc_tips from anyone without
// `view_cash_detail` — this component just renders what it's given and uses
// `can_see_detail` to decide whether the reconciliation figures show.
// Mon–Sun. Must match DAYS_IN_PAYROLL_WEEK in server/features/payroll/cash-gate.js
// — the client and the certify gate disagreeing is how you get a button that
// looks available and then 409s.
const DAILY_DAYS_PER_WEEK = 7;

export function WeeklyCashCountView({ currentUser }) {
  const headers = { 'x-user-id': String(currentUser.id) };
  // Payroll weeks (Mon–Sun), most-recent first — starting with the CURRENT
  // in-progress week so this week's entered counts are visible (CJ 2026-07-09:
  // the dropdown must include this week, not only completed weeks).
  const periods = useMemo(() => {
    const out = [];
    const d = new Date(); d.setUTCHours(12, 0, 0, 0);
    const dow = d.getUTCDay(); const off = dow === 0 ? 6 : dow - 1;
    d.setUTCDate(d.getUTCDate() - off); // this week's Monday (in progress)
    for (let i = 0; i < 9; i++) {
      const start = new Date(d); start.setUTCDate(start.getUTCDate() - 7 * i);
      const end = new Date(start); end.setUTCDate(end.getUTCDate() + 6);
      out.push({
        weekStart: start.toISOString().slice(0, 10),
        weekEnd: end.toISOString().slice(0, 10),
        current: i === 0,
      });
    }
    return out;
  }, []);
  const [weekStart, setWeekStart] = useState(() => periods[0]?.weekStart || '');
  const [rows, setRows] = useState([]);
  const [edits, setEdits] = useState({});     // { [locId]: string }
  const [busy, setBusy] = useState(true);
  const [savingLoc, setSavingLoc] = useState(null);
  const [err, setErr] = useState('');

  const [history, setHistory] = useState(null);
  async function loadHistory() {
    try {
      const r = await api('GET', '/api/admin/payroll/cash-counts/history?weeks=12', null, headers);
      setHistory(Array.isArray(r?.history) ? r.history : []);
    } catch { setHistory(null); } // shift leads 403 → panel simply absent
  }
  async function load() {
    setBusy(true); setErr('');
    try {
      const r = await api('GET', `/api/admin/payroll/cash-counts?week_start=${weekStart}`, null, headers);
      setRows(Array.isArray(r?.counts) ? r.counts : []);
      setEdits({});
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setBusy(false); }
  }
  useEffect(() => { if (weekStart) load(); }, [weekStart]);
  useEffect(() => { loadHistory(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  async function saveCount(locId, fallback) {
    const v = edits[locId] ?? fallback;
    if (v == null || v === '' || !Number.isFinite(Number(v))) return;
    setSavingLoc(locId); setErr('');
    try {
      await api('PUT', `/api/admin/payroll/cash-counts/${locId}`,
        { week_start: weekStart, counted_cash: Number(v) }, headers);
      await load();
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setSavingLoc(null); }
  }
  // Pull-from-Square (expected cash + CC tips) lives on the Payroll view now
  // (CJ 2026-07-28) — Cash counts is for counting the drawer + certifying.
  async function toggleCertify(locId, certified) {
    setSavingLoc(locId); setErr('');
    try {
      await api('PUT', `/api/admin/payroll/cash-counts/${locId}/certify`,
        { week_start: weekStart, certified }, headers);
      await load();
    } catch (e) { setErr(e?.message || String(e)); }
    finally { setSavingLoc(null); }
  }

  const usd = (n) => (n == null || n === '' ? '—' : '$' + Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
  const fmtWeek = (p) => {
    const f = (s) => new Date(s + 'T12:00:00Z').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return `${f(p.weekStart)} – ${f(p.weekEnd)}${p.current ? ' · this week' : ''}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, maxWidth: 620, margin: '0 auto', width: '100%' }}>
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="text-base font-semibold">Weekly cash count</div>
            <div className="text-xs text-gray-500">Enter the week's counted cash for your store, then certify it.</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <select value={weekStart} onChange={(e) => setWeekStart(e.target.value)}
              aria-label="Payroll week"
              style={{ padding: '6px 8px', fontSize: 13, border: '1px solid #d0d4d9', borderRadius: 6, background: '#fff' }}>
              {periods.map((p) => <option key={p.weekStart} value={p.weekStart}>{fmtWeek(p)}</option>)}
            </select>
          </div>
        </div>
      </div>

      {err && <div style={{ background: '#fee', color: '#b91c1c', padding: 10, borderRadius: 8, fontSize: 12.5 }}>{err}</div>}
      {busy && rows.length === 0 && <div className="card text-sm text-gray-500">Loading…</div>}

      {rows.map((c) => {
        const certified = !!c.certified_at;
        const edited = edits[c.location_id];
        const dailyFill = c.counted_cash == null && c.daily_total != null && c.daily_total > 0
          ? String(c.daily_total) : null;
        const shown = edited != null ? edited
          : (c.counted_cash != null ? String(c.counted_cash) : (dailyFill ?? ''));
        const dirty = (edited != null && edited !== (c.counted_cash != null ? String(c.counted_cash) : ''))
          || (edited == null && dailyFill != null);
        const variance = c.counted_cash != null && c.daily_total != null && c.daily_total > 0
          ? Number((Number(c.counted_cash) - c.daily_total).toFixed(2)) : null;
        // D225: the server refuses to certify a week with no counted amount.
        // Mirror its rule exactly so the button is honest rather than the
        // manager discovering it in a 409 — a typed-but-unsaved figure is not
        // a counted week, which is why this reads counted_cash and not `shown`.
        const cashOk = c.counted_cash != null
          || (c.daily_certified_days || 0) >= DAILY_DAYS_PER_WEEK;
        return (
          <div className="card" key={c.location_id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ fontWeight: 700 }}>{c.location_name}</div>
              {certified
                ? <span style={{ fontSize: 11, fontWeight: 700, color: '#226b4a', background: '#e6f2ec', border: '1px solid #a8d3bb', borderRadius: 999, padding: '3px 10px' }}>
                    ✓ Certified{c.certified_by_name ? ` · ${c.certified_by_name}` : ''}
                  </span>
                : <span style={{ fontSize: 11, fontWeight: 700, color: '#8a6b1a', background: '#fbf7ec', border: '1px solid #eee2c4', borderRadius: 999, padding: '3px 10px' }}>
                    Not certified
                  </span>}
            </div>

            {dailyFill != null && (
              <div style={{ fontSize: 12, color: '#8a6d1a', background: '#faf5e4', borderRadius: 6, padding: '6px 10px' }}>
                Pre-filled from {c.daily_days} daily count{c.daily_days === 1 ? '' : 's'} (${c.daily_total.toFixed(2)}).
                Adjust if the safe count differs, then Save.
              </div>
            )}
            {variance != null && Math.abs(variance) >= 0.01 && (
              <div style={{ fontSize: 12, color: '#a8323a', background: '#faeceb', borderRadius: 6, padding: '6px 10px' }}>
                Weekly entry differs from the daily-count total (${c.daily_total.toFixed(2)}) by
                {' '}{variance > 0 ? '+' : ''}${variance.toFixed(2)}. Weekly stays authoritative — worth a look.
              </div>
            )}
            <label style={{ fontSize: 12, color: '#6b7280', fontWeight: 600 }}>
              Counted cash (this week)
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9e9e9e' }}>$</span>
                  <input type="number" inputMode="decimal" min="0" step="0.01"
                    value={shown} placeholder="0.00" disabled={certified}
                    onChange={(e) => setEdits((s) => ({ ...s, [c.location_id]: e.target.value }))}
                    style={{ width: '100%', padding: '10px 10px 10px 22px', fontSize: 16, textAlign: 'right',
                             border: '1px solid #d0d4d9', borderRadius: 8, background: certified ? '#f5f5f5' : '#fff',
                             fontVariantNumeric: 'tabular-nums' }} />
                </div>
                <button onClick={() => saveCount(c.location_id, shown)} disabled={!dirty || savingLoc === c.location_id || certified}
                  style={{ padding: '0 16px', borderRadius: 8, border: 0, fontWeight: 700, fontSize: 13,
                           background: dirty && !certified ? '#b48a47' : '#e0e0e0', color: '#fff',
                           cursor: dirty && !certified ? 'pointer' : 'default' }}>
                  {savingLoc === c.location_id ? '…' : 'Save'}
                </button>
              </div>
            </label>

            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              {!certified && !cashOk && (
                <div style={{ fontSize: 12, color: '#8a6d1a', flex: '1 1 240px' }}>
                  {dirty
                    ? 'Save the counted cash before certifying.'
                    : `Enter this week's counted cash before certifying — or certify all ${DAILY_DAYS_PER_WEEK} daily drawer counts` +
                      `${c.daily_certified_days ? ` (${c.daily_certified_days} of ${DAILY_DAYS_PER_WEEK} done)` : ''}.`}
                </div>
              )}
              {certified
                ? <button onClick={() => toggleCertify(c.location_id, false)} disabled={savingLoc === c.location_id}
                    style={{ background: 'none', border: 0, color: '#666', fontSize: 12.5, textDecoration: 'underline', cursor: 'pointer', padding: 4 }}>
                    Un-certify
                  </button>
                : <button onClick={() => toggleCertify(c.location_id, true)}
                    disabled={savingLoc === c.location_id || !cashOk}
                    style={{ padding: '9px 16px', borderRadius: 8, border: 0, fontWeight: 700, fontSize: 13,
                             background: '#226b4a', color: '#fff', cursor: cashOk ? 'pointer' : 'default',
                             opacity: cashOk ? 1 : 0.5 }}>
                    Mark counted &amp; certify
                  </button>}
            </div>
          </div>
        );
      })}

      {history && history.length > 0 && (
        <div className="card">
          <div className="text-sm font-semibold mb-1">Over/short &amp; tips per hour — last 12 weeks</div>
          <div className="text-xs text-gray-500 mb-2">
            Over/short = counted − expected cash for the week (red = short). This was computed at every payroll
            run and thrown away until now; a drawer that's short the same day every week is a pattern, not luck.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ fontSize: 12.5, borderCollapse: 'collapse', minWidth: 480 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#757575', fontSize: 11.5 }}>
                  <th style={{ padding: '3px 12px 3px 0' }}>Week</th>
                  <th style={{ padding: '3px 12px 3px 0' }}>Store</th>
                  <th style={{ padding: '3px 12px 3px 0', textAlign: 'right' }}>Counted</th>
                  <th style={{ padding: '3px 12px 3px 0', textAlign: 'right' }}>Expected</th>
                  <th style={{ padding: '3px 12px 3px 0', textAlign: 'right' }}>Over/short</th>
                  <th style={{ padding: '3px 0', textAlign: 'right' }}>Tips/hr</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #f2f2f2' }}>
                    <td style={{ padding: '2px 12px 2px 0', whiteSpace: 'nowrap' }}>{h.week_start}</td>
                    <td style={{ padding: '2px 12px 2px 0' }}>{h.location}</td>
                    <td style={{ padding: '2px 12px 2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.counted != null ? `$${h.counted.toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '2px 12px 2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.expected != null ? `$${h.expected.toFixed(2)}` : '—'}</td>
                    <td style={{ padding: '2px 12px 2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600,
                                 color: h.over_short == null ? '#9e9e9e' : h.over_short < -1 ? '#b91c1c' : '#2e7d32' }}>
                      {h.over_short != null ? `${h.over_short < 0 ? '−' : '+'}$${Math.abs(h.over_short).toFixed(2)}` : '—'}
                    </td>
                    <td style={{ padding: '2px 0', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{h.tips_per_hour != null ? `$${Number(h.tips_per_hour).toFixed(2)}` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!busy && rows.length === 0 && (
        <div className="card text-sm text-gray-500">No stores assigned to you for cash counts.</div>
      )}

      {/* Daily drawer counts + certify — the per-day grid (CJ 2026-07-28). */}
      <DailyCashCountsPanel
        weekStart={weekStart}
        weekEnd={periods.find((p) => p.weekStart === weekStart)?.weekEnd || weekStart}
        currentUser={currentUser} />
    </div>
  );
}
